import { useState } from "react";
import { ArrowRight, Sparkles, ShieldCheck, Gift, Bot, Wallet } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import AffiliateSignupDialog from "@/components/marketing/AffiliateSignupDialog";
import { DERIV_AFFILIATE_URL } from "@/lib/socialLinks";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/d6d87f76-c4a8-410a-8b53-e45e296abac9/images/1326190b2be6a839e76400aa54a507983eba68d68b35e6f7c5dae765877d6ba6.png";

const STATS = [
  { icon: Gift, label: "100% free to use", sub: "No charge, ever" },
  { icon: Wallet, label: "$10,000 free demo", sub: "Practice with virtual funds" },
  { icon: Bot, label: "Automated bots", sub: "You set the limits" },
];

const Hero = () => {
  const navigate = useNavigate();
  const [signupOpen, setSignupOpen] = useState(false);

  const handleSignupClick = () => setSignupOpen(true);
  const goToDesk = () => navigate("/trade?connect=1");

  return (
    <section
      data-testid="hero-section"
      className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden vts-noise"
    >
      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <img
          src={HERO_IMG}
          alt=""
          className="w-full h-full object-cover opacity-50"
          style={{ mixBlendMode: "screen" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/70 to-black" />
        <div className="absolute inset-0 vts-grid-bg opacity-40" />
        {/* floating glow orbs */}
        <div className="absolute top-24 right-[12%] h-56 w-56 rounded-full bg-vts-blue/20 blur-[90px] animate-float" />
        <div className="absolute bottom-10 left-[8%] h-44 w-44 rounded-full bg-emerald-500/10 blur-[80px] animate-float" style={{ animationDelay: "1.4s" }} />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-5 lg:px-8">
        <div className="max-w-3xl">
          {/* badges */}
          <div className="hero-reveal flex flex-wrap items-center gap-3 mb-7" style={{ animationDelay: "0.05s" }}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-vts-line bg-vts-surface/60 backdrop-blur-sm">
              <Sparkles className="h-3.5 w-3.5 text-vts-blue" />
              <span className="text-xs uppercase tracking-[0.2em] text-vts-silver font-medium">
                Official Deriv Partner
              </span>
            </div>
            <div
              data-testid="hero-free-badge"
              className="free-badge inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-emerald-400/50 bg-emerald-500/15 backdrop-blur-sm"
            >
              <Gift className="h-3.5 w-3.5 text-emerald-400" />
              <span className="text-xs uppercase tracking-[0.18em] text-emerald-300 font-bold">
                100% Free — No charge, ever
              </span>
            </div>
          </div>

          <h1
            data-testid="hero-headline"
            className="hero-reveal font-heading font-bold text-5xl sm:text-6xl lg:text-7xl text-white tracking-tighter leading-[0.95]"
            style={{ animationDelay: "0.15s" }}
          >
            Trade Forex &amp; Gold<br />
            <span className="text-vts-silver">on </span>
            <span className="animate-gradient-text">your terms.</span>
          </h1>

          <p
            className="hero-reveal mt-7 text-base sm:text-lg text-vts-silver/80 max-w-2xl leading-relaxed font-body"
            style={{ animationDelay: "0.28s" }}
          >
            <span className="text-white font-semibold">Automated bots with limits you control. An arcade where every game is a real trade. A pro desk with live signals.</span>{" "}
            All of it <span className="text-emerald-400 font-semibold">100% free</span> — no subscription, no hidden fees.
            Just connect a free Deriv account, start on the{" "}
            <span className="text-white font-medium">$10,000 demo</span>, and go live when you're ready.
          </p>

          <div className="hero-reveal mt-10 flex flex-wrap items-center gap-4" style={{ animationDelay: "0.4s" }}>
            <Button
              data-testid="hero-create-account-btn"
              onClick={handleSignupClick}
              size="lg"
              className="btn-shimmer bg-vts-blue hover:bg-vts-blueHover text-white h-12 px-7 rounded-md font-semibold text-sm tracking-wide animate-pulse-glow"
            >
              Create your free account
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <button
              data-testid="hero-open-desk-btn"
              onClick={goToDesk}
              className="inline-flex items-center gap-2 text-vts-silver hover:text-white transition font-medium text-sm px-5 py-3 border border-vts-line rounded-md hover:bg-vts-surface hover:border-vts-blue"
            >
              Already have a Deriv account? <span className="text-vts-blue">Open VTS Desk →</span>
            </button>
          </div>

          <p className="hero-reveal mt-3 text-xs text-vts-silver/60 font-body" style={{ animationDelay: "0.5s" }}>
            In a hurry?{" "}
            <a data-testid="hero-direct-affiliate-link" href={DERIV_AFFILIATE_URL}
              className="text-vts-blue hover:text-white underline underline-offset-2 transition">
              Sign up on Deriv directly →
            </a>{" "}
            (our official partner link — free demo with $10,000)
          </p>

          {/* stats strip */}
          <div
            data-testid="hero-stats-strip"
            className="hero-reveal mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-2xl"
            style={{ animationDelay: "0.6s" }}
          >
            {STATS.map((s) => (
              <div
                key={s.label}
                className="flex items-center gap-3 rounded-lg border border-vts-line bg-vts-surface/50 backdrop-blur-sm px-4 py-3 hover:border-vts-blue/50 transition"
              >
                <s.icon className="h-5 w-5 text-vts-blue shrink-0" />
                <div>
                  <div className="text-sm font-semibold text-white leading-tight">{s.label}</div>
                  <div className="text-[11px] text-vts-silver/60">{s.sub}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="hero-reveal mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs text-vts-silver/70" style={{ animationDelay: "0.7s" }}>
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-vts-blue" />
              <span>Regulated broker network</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-vts-profit animate-pulse" />
              <span>Live market data — Deriv API</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-vts-silver">$0</span>
              <span>commission to open account</span>
            </div>
          </div>
        </div>
      </div>
      <AffiliateSignupDialog open={signupOpen} onOpenChange={setSignupOpen} />
    </section>
  );
};

export default Hero;
